import {
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  IconButton,
  Button,
  Select,
  MenuItem,
  LinearProgress,
  DialogActions,
  DialogContent,
  TextField,
  DialogTitle,
  Dialog,
  Pagination,
  Stack,
  PaginationItem
} from "@mui/material";

import React, { useEffect, useState, Fragment } from "react";
import { getDateTimeWith12R } from "../../../utils/format_date";
import {
  Backup as BackupIcon,
  SettingsBackupRestore as SettingsBackupRestoreIcon
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  getBackUpDetails,
  startBackup,
  restoreNow,
  setBUInterval
} from "../../../redux/modules/manage";
import { setAlert } from "../../../redux/modules/alert";
import formatFileSize from "../../../utils/formatFileSize";

function BUItem({ bu }) {
  const dispatch = useDispatch();
  const [restoring, setRestoring] = useState(false);

  // Two-step confirmation states
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  const [typedValue, setTypedValue] = useState("");

  // Step 1: User clicks Restore button → open first confirm dialog
  const handleRestoreClick = () => {
    setConfirmOpen(true);
  };

  // Step 2: User confirms YES → open typing dialog
  const handleConfirmYes = () => {
    setConfirmOpen(false);
    setTypeOpen(true);
  };

  // Step 1: User says NO → close first dialog
  const handleConfirmNo = () => {
    setConfirmOpen(false);
  };

  // Step 3: User types "restore" & clicks final confirm
  const handleFinalRestore = () => {
    if (typedValue !== "restore") return; // safety guard

    setTypeOpen(false);
    setTypedValue("");
    setRestoring(true);

    dispatch(restoreNow(bu.id))
      .then(() => {
        setRestoring(false);
        dispatch(setAlert(`Data restored successfully`, "success"));
      })
      .catch((error) => {
        dispatch(
          setAlert(
            error?.response?.data?.message ||
              `Failed to restore data`,
            "error"
          )
        );
        setRestoring(false);
      });
  };

  return (
    <>
      {/* List Item Row */}
      <ListItem>
        <ListItemIcon>
          {bu.type === "auto" ? (
            <SettingsBackupRestoreIcon className="bu-logo" />
          ) : null}

          {bu.type === "manual" ? <BackupIcon className="bu-logo" /> : null}
        </ListItemIcon>

        <ListItemText
          primary={
            <div>
              <Typography>{bu.file_name}</Typography>
              <div className="size-n-create">
                <Typography variant="caption">Size: {formatFileSize(bu.file_size)}</Typography>
                <Typography variant="caption">
                  Created: {getDateTimeWith12R(bu.created_at)}
                </Typography>
              </div>
            </div>
          }
        />

        <ListItemSecondaryAction>
          <div className="restore-bu">
            <Button
              variant="outlined"
              color="primary"
              onClick={handleRestoreClick}
              disabled={restoring}
            >
              Restore
            </Button>

            {restoring ? <LinearProgress className="restore-lp" /> : null}
          </div>
        </ListItemSecondaryAction>
      </ListItem>

      {/* FIRST CONFIRMATION DIALOG */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Restore</DialogTitle>
        <DialogContent>
          <Typography>
            Restoring this backup will overwrite your existing data.
            <br />
            Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmNo}>Cancel</Button>
          <Button color="error" onClick={handleConfirmYes}>
            Yes, Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* SECOND TYPING DIALOG */}
      <Dialog open={typeOpen} onClose={() => setTypeOpen(false)}>
        <DialogTitle>Type "restore" to continue</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            value={typedValue}
            onChange={(e) => setTypedValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTypeOpen(false)}>Cancel</Button>

          <Button
            variant="contained"
            color="error"
            disabled={typedValue !== "restore"}
            onClick={handleFinalRestore}
          >
            Restore
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function Backup() {
  const dispatch = useDispatch();
  const [buInterval, setBuInterval] = useState("");
  const [loading, setLoading] = useState(false);
  const [settingInterval, setSI] = useState(false);
  const [backing, setBacking] = useState(false);
  const backups = useSelector((state) => state.manage.backups);
  const interval = useSelector((state) => state.manage.interval);
  const pagination = useSelector((state) => state.manage.pagination);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    if (buInterval != interval) {
      setBuInterval(interval);
    }
  }, [interval]);

  useEffect(() => {
    setLoading(true);
    dispatch(getBackUpDetails(page, limit))
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  }, [page, limit]);

  const onBuIntChange = (e) => {
    const newValue = e.target.value === "select" ? "" : e.target.value;

    setBuInterval(newValue);
    setSI(true);
    dispatch(setBUInterval(newValue))
      .then(() => {
        setSI(false);
      })
      .catch(() => {
        setSI(false);
      });
  };

  const onBackUp = () => {
    setBacking(true);
    dispatch(startBackup())
      .then(() => {
        dispatch(setAlert(`Data restored successfully`, "success"));
        setBacking(false);
      })
      .catch((error) => {
        dispatch(
          setAlert(
            error?.response?.data?.message ||
              `Failed to backup data`,
            "error"
          )
        );
        setBacking(false);
      });
  };

  return (
    <div>
      <div className="tble-bdr log-l-cont">
        <List className="log-list">
          {backups.map((bu, b) => (
            <Fragment key={b}>
              <BUItem bu={bu} />
              <Divider />
            </Fragment>
          ))}
          {!backups.length & !loading ? (
            <Typography
              variant="caption"
              className="empty-message"
              style={{ margin: "15px" }}
              component="div"
            >
              No backups found...
            </Typography>
          ) : null}
          <ListItem>
            <div className="bu-change">
              <div className="bu-action1">
                <Typography>Automatically backup database:</Typography>
                <div className="auto-bu-select">
                  <Select
                    style={{ maxHeight: 40 }}
                    variant="outlined"
                    margin="dense"
                    value={buInterval || "select"}
                    onChange={onBuIntChange}
                    disabled={settingInterval}
                  >
                    <MenuItem value="1">Daily</MenuItem>
                    <MenuItem value="7">Weekly</MenuItem>
                    <MenuItem value="30">Monthly</MenuItem>
                    <MenuItem value="365">Annually</MenuItem>
                  </Select>
                  {settingInterval ? (
                    <LinearProgress className="select-progress" />
                  ) : null}
                </div>
              </div>
              <Divider orientation="vertical" flexItem />
              <div className="bu-action2">
                <Button
                  variant="contained"
                  color="primary"
                  disabled={backing}
                  onClick={onBackUp}
                >
                  Backup now
                </Button>
                {backing ? <LinearProgress className="btn-progress" /> : null}
              </div>
            </div>
          </ListItem>
        </List>
      </div>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent={"end"}
        sx={{ marginTop: "20px" }}
      >
        <Typography>Rows Per Page:</Typography>
        <Select
          size="small"
          value={limit}
          onChange={(e) => {
            setLimit(e.target.value);
            setPage(1);
          }}
        >
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={20}>20</MenuItem>
          <MenuItem value={50}>50</MenuItem>
        </Select>
        <Pagination
          count={pagination?.total_pages || 1}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
          siblingCount={1}
          boundaryCount={1}
          renderItem={(item) => {
            if (item.type === "previous") {
              return <PaginationItem {...item} disabled={page === 1} />;
            }

            if (item.type === "next") {
              return (
                <PaginationItem
                  {...item}
                  disabled={page === (pagination?.total_pages || 1)}
                />
              );
            }

            return <PaginationItem {...item} />;
          }}
        />
      </Stack>
    </div>
  );
}

export default Backup;
